import React, { useEffect, useState } from 'react';
import { ConsentLayout } from './ConsentLayout';
import { ProviderSelectionScreen, Bank } from './screens/ProviderSelectionScreen';
import { ConsentDetailsScreen } from './screens/ConsentDetailsScreen';
import { ConsentDurationScreen } from './screens/ConsentDurationScreen';
import { RedirectionScreen } from './screens/RedirectionScreen';
import { FaceIDScreen } from './screens/FaceIDScreen';
import { AccountSelectionScreen, Account } from './screens/AccountSelectionScreen';
import { ConsentSuccessScreen } from './screens/ConsentSuccessScreen';
import { ConsentManagementScreen } from './screens/ConsentManagementScreen';
import { ConsentDetailedView } from './screens/ConsentDetailedView';
import { LFIConsentManagementScreen } from './screens/LFIConsentManagementScreen';
import { LFIConsentDetailedView } from './screens/LFIConsentDetailedView';
import { ErrorScreen, ErrorCode } from './screens/ErrorScreen';
import { PFMBeforeScreen } from './screens/PFMBeforeScreen';
import { PFMAfterScreen } from './screens/PFMAfterScreen';
import { DemoLandingScreen } from './screens/DemoLandingScreen';
import { BillSelectionScreen } from './screens/BillSelectionScreen';
import { BankConnectionSelectionScreen, ConnectedBank } from './screens/BankConnectionSelectionScreen';
import { VRPSetupScreen, VRPConfig } from './screens/VRPSetupScreen';
import { VRPPermissionScreen } from './screens/VRPPermissionScreen';
import { VRPAuthorizationScreen } from './screens/VRPAuthorizationScreen';
import { VRPSuccessScreen } from './screens/VRPSuccessScreen';
import { DemoSwitcher } from './ui/DemoSwitcher';
import { CMITopBar } from './ui/CMITopBar';
import { UAE_DATA_CLUSTERS, calculateExpiryDate, calculateNextPaymentDate, ConsentDetails, ConsentState, VRPPayment } from '../constants/uaeStandards';
import { getBankTheme, DEFAULT_BANK_THEME, getShortBankName } from '../constants/bankThemes';
import { MOCK_DATA, UpcomingBill } from '../constants/mockData';
type Step = 'demo-landing' | 'pfm-before' | 'provider-selection' | 'consent-duration' | 'consent-details' | 'redirect-to-lfi' | 'mfa-verification' | 'account-selection' | 'redirect-to-tpp' | 'success' | 'pfm-after' | 'consent-management' | 'consent-detailed-view' | 'lfi-consent-management' | 'lfi-consent-detailed-view' | 'bill-selection' | 'bank-connection-selection' | 'vrp-provider-selection' | 'vrp-setup' | 'vrp-permission' | 'vrp-redirect-to-lfi' | 'vrp-authorization' | 'vrp-bank-auth' | 'vrp-redirect-to-tpp' | 'vrp-tpp-auth' | 'vrp-success' | 'error';
type DemoMode = 'TPP' | 'LFI';
interface ConsentJourneyDemoProps {
  onComplete?: () => void;
  onStartDataSharingFlow?: () => void;
  onStartVRPFlow?: () => void;
  onSkipToApp?: () => void;
}
// VeloX logo
const VELOX_LOGO = 'https://cdn.magicpatterns.com/uploads/6ixnVN3mm3sFs3kb5m4Wow/apple-touch-icon.png';
const MOCK_BANKS: Bank[] = [{
  id: 'adcb',
  name: 'Abu Dhabi Commercial Bank',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/wpkTMSY3dU4JPGR41Jm2TG/ADCB_icon.svg'
}, {
  id: 'emiratesnbd',
  name: 'Emirates NBD',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/91j45KU69fJgTXQvxBLPwL/ENBD.jpg'
}, {
  id: 'adib',
  name: 'ADIB',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tNEFVG5j2DGJtEABNU4nN9/ADIB.jpg'
}, {
  id: 'fab',
  name: 'First Abu Dhabi Bank',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ii5ZfgQSfFd3JdiuLGJR9e/First-Abu-Dhabi-Bank.svg'
}, {
  id: 'mashreq',
  name: 'Mashreq Bank',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jubwaaX5WAee9nTRWWTwB8/Mashreg.jpg'
}, {
  id: 'hsbc',
  name: 'HSBC',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kvYE93vya43grBRKocirfC/HSBC.jpg'
}, {
  id: 'cbd',
  name: 'Commercial Bank of Dubai',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rDfgkRZkWdt4x2YFi9F9RA/CBD.svg'
}, {
  id: 'dib',
  name: 'Dubai Islamic Bank',
  logoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nKHQGwNUoGJDqPAPwEYtDe/DIB.png'
}];
const MOCK_ACCOUNTS: Account[] = [{
  id: '1',
  name: 'Current Account',
  accountNumber: '4627033234562456',
  balance: 5000,
  type: 'Balance'
}, {
  id: '2',
  name: 'Savings',
  accountNumber: '4627033234567891',
  balance: 25000,
  type: 'Balance'
}, {
  id: '3',
  name: 'Overdraft',
  accountNumber: '4627033234561234',
  balance: 1500,
  type: 'Overdraft'
}];
const MOCK_UNAVAILABLE_ACCOUNTS: Account[] = [{
  id: '4',
  name: 'Credit Card',
  accountNumber: '4627033234569999',
  balance: 0,
  type: 'Credit',
  available: false,
  unavailableReason: 'Account temporarily locked'
}];
const MOCK_PERMISSIONS = [{
  id: '1',
  label: 'Your Account details',
  icon: 'user' as const
}, {
  id: '2',
  label: 'Your Regular Payments',
  icon: 'card' as const
}, {
  id: '3',
  label: 'Your Account Transactions',
  icon: 'file' as const
}, {
  id: '4',
  label: 'Your Product Information',
  icon: 'info' as const
}, {
  id: '5',
  label: 'Contact and Party Details',
  icon: 'users' as const
}];
const MOCK_LFI_PERMISSIONS = ['Your Account details', 'Your Regular Payments', 'Your Account Transactions', 'Your Product Information', 'Contact and Party Details'];
export function ConsentJourneyDemo({
  onComplete,
  onStartDataSharingFlow,
  onStartVRPFlow,
  onSkipToApp
}: ConsentJourneyDemoProps = {}) {
  const [currentStep, setCurrentStep] = useState<Step>('demo-landing');
  const [demoMode, setDemoMode] = useState<DemoMode>('TPP');
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [connectedBanks, setConnectedBanks] = useState<Bank[]>([]);
  const [consentDuration, setConsentDuration] = useState(6);
  const [errorState, setErrorState] = useState<{
    code: ErrorCode;
    message?: string;
  } | null>(null);
  // VRP-specific state
  const [selectedBill, setSelectedBill] = useState<UpcomingBill | null>(null);
  const [vrpConfig, setVRPConfig] = useState<VRPConfig | null>(null);
  const [vrpPaymentAccount, setVRPPaymentAccount] = useState<Account | null>(null);
  // Initialize with mock data
  const [activeConsents, setActiveConsents] = useState<ConsentDetails[]>(MOCK_DATA.active);
  const [historyConsents, setHistoryConsents] = useState<ConsentDetails[]>(MOCK_DATA.history);
  const [pendingConsents, setPendingConsents] = useState<ConsentDetails[]>(MOCK_DATA.pending);
  const [selectedConsentId, setSelectedConsentId] = useState<string | null>(null);
  const [consentCreated, setConsentCreated] = useState(false);
  const tppName = 'VeloX';
  const expiryDate = calculateExpiryDate(consentDuration);
  // Get bank theme for LFI screens
  const bankTheme = selectedBank ? getBankTheme(selectedBank.id) || DEFAULT_BANK_THEME : DEFAULT_BANK_THEME;
  // Show demo switcher on CMI screens
  const showDemoSwitcher = ['pfm-after', 'consent-management', 'lfi-consent-management'].includes(currentStep);
  // Initialize connected banks from active consents
  useEffect(() => {
    const uniqueBanks = new Set<string>();
    const banks: Bank[] = [];
    activeConsents.forEach(consent => {
      if (!uniqueBanks.has(consent.bankName)) {
        uniqueBanks.add(consent.bankName);
        const mockBank = MOCK_BANKS.find(b => b.name === consent.bankName);
        if (mockBank) {
          banks.push(mockBank);
        }
      }
    });
    setConnectedBanks(banks);
  }, [activeConsents]);
  const currentBank = connectedBanks[0] || MOCK_BANKS[0];
  const handleStartDataSharingJourney = () => {
    if (onStartDataSharingFlow) {
      onStartDataSharingFlow();
    }
    setCurrentStep('pfm-before');
  };
  const handleStartVRPJourney = () => {
    if (onStartVRPFlow) {
      onStartVRPFlow();
    }
    // Start at bill selection screen (guided journey)
    setCurrentStep('bill-selection');
  };
  const handleViewDashboard = () => {
    if (onComplete) {
      onComplete();
    } else {
      setCurrentStep('pfm-after');
    }
  };
  // VRP Flow Handlers
  const handleBillSelected = (bill: UpcomingBill) => {
    setSelectedBill(bill);
    setCurrentStep('vrp-setup');
  };
  const handlePayBill = (billId: string) => {
    const bill = MOCK_DATA.upcomingBills.find(b => b.id === billId);
    if (bill) {
      setSelectedBill(bill);
      // For one-time payment, we'd go directly to payment
      // For now, redirect to VRP setup
      setCurrentStep('vrp-setup');
    }
  };
  const handleSetupAutoPay = (billId: string) => {
    const bill = MOCK_DATA.upcomingBills.find(b => b.id === billId);
    if (bill) {
      setSelectedBill(bill);
      setCurrentStep('vrp-setup');
    }
  };
  const handleVRPConfigured = (config: VRPConfig, bankId?: string) => {
    setVRPConfig(config);
    if (bankId) {
      // User selected existing bank - skip to authorization
      const bank = connectedBanks.find(b => b.id === bankId);
      if (bank) {
        setSelectedBank(bank);
        // Go straight to authorization (skip provider selection and bank auth)
        setCurrentStep('vrp-authorization');
      }
    } else {
      // User wants to connect new bank - go to provider selection
      setCurrentStep('vrp-provider-selection');
    }
  };
  const handleSelectExistingBank = (bankId: string) => {
    // User selected an existing connected bank
    // Skip bank authentication and go directly to account selection
    const bank = connectedBanks.find(b => b.id === bankId);
    if (bank) {
      setSelectedBank(bank);
      setCurrentStep('vrp-account-selection');
    }
  };
  const handleConnectNewBank = () => {
    // User wants to connect a new bank
    // Show bank selection screen first (VRP version)
    setCurrentStep('vrp-provider-selection');
  };
  const handleVRPBankSelected = (bank: Bank) => {
    // User selected a bank for VRP
    setSelectedBank(bank);
    // Now redirect to that bank for authentication
    setCurrentStep('vrp-redirect-to-lfi');
    setTimeout(() => setCurrentStep('vrp-bank-auth'), 2000);
  };
  const handleVRPAuthorized = () => {
    // This is now only called from VRPPermissionScreen when using existing bank
    setCurrentStep('vrp-redirect-to-lfi');
    setTimeout(() => setCurrentStep('vrp-bank-auth'), 2000);
  };
  const handleVRPBankAuthComplete = () => {
    // After Face ID, go to VRP Authorization screen
    setCurrentStep('vrp-authorization');
  };
  const handleVRPAuthorizationComplete = (accountId: string) => {
    // User authorized VRP with selected account
    const selectedAccount = MOCK_ACCOUNTS.find(acc => acc.id === accountId);
    setVRPPaymentAccount(selectedAccount || null);
    // After authorization, show Face ID authentication FIRST (before redirect)
    setCurrentStep('vrp-tpp-auth');
  };
  const handleVRPTPPAuthComplete = () => {
    // After Face ID, redirect to TPP
    setCurrentStep('vrp-redirect-to-tpp');
    setTimeout(() => {
      // After redirect animation, create consent and show success
      if (selectedBill && vrpConfig && vrpPaymentAccount && selectedBank) {
        const firstPaymentDate = new Date().toLocaleDateString('en-GB');
        const nextPaymentDate = calculateNextPaymentDate(firstPaymentDate, vrpConfig.frequency);
        const newVRPConsent: ConsentDetails = {
          id: Date.now().toString(),
          consentId: `VRP${Date.now()}${Math.random().toString(36).substring(7)}`,
          type: 'ServiceInitiation',
          state: 'Authorized',
          tppName,
          tppTradingName: tppName,
          tppLegalName: 'VeloX Financial Services LLC',
          tppLogo: VELOX_LOGO,
          merchantName: selectedBill.merchantName,
          bankName: selectedBank.name,
          bankLogo: selectedBank.logoUrl,
          accountType: vrpPaymentAccount.name,
          accountNumber: vrpPaymentAccount.accountNumber,
          accountLastFour: vrpPaymentAccount.accountNumber.slice(-4),
          connectedDate: new Date().toLocaleDateString('en-GB'),
          authorizedDate: new Date().toLocaleDateString('en-GB'),
          expiryDate: calculateExpiryDate(12),
          lastAccessDate: new Date().toISOString(),
          durationMonths: 12,
          dataClusters: [],
          selectedAccounts: [{
            id: vrpPaymentAccount.id,
            name: vrpPaymentAccount.name,
            accountNumber: vrpPaymentAccount.accountNumber,
            accountType: vrpPaymentAccount.name
          }],
          vrpDetails: {
            frequency: vrpConfig.frequency,
            maxAmountPerPayment: vrpConfig.maxAmountPerPayment,
            totalPaymentsAllowed: vrpConfig.totalPaymentsAllowed,
            paymentsCompleted: 0,
            firstPaymentDate,
            nextPaymentDate,
            reference: selectedBill.reference,
            paymentPurpose: 'utility-bill',
            merchantName: selectedBill.merchantName,
            merchantLogo: selectedBill.merchantLogo,
            allowBalanceCheck: true
          },
          paymentHistory: [{
            id: `vrp-pay-${Date.now()}`,
            date: nextPaymentDate,
            amount: selectedBill.amount,
            currency: selectedBill.currency,
            status: 'scheduled',
            reference: selectedBill.reference,
            description: `${selectedBill.merchantName} - First scheduled payment`,
            recipient: selectedBill.merchantName
          }],
          createdBy: 'User',
          lastModified: new Date().toISOString(),
          version: 1
        };
        setActiveConsents([...activeConsents, newVRPConsent]);
      }
      setCurrentStep('vrp-success');
    }, 2000);
  };
  const handleDemoModeChange = (mode: DemoMode) => {
    setDemoMode(mode);
    if (mode === 'TPP') {
      setCurrentStep('consent-management');
    } else {
      setCurrentStep('lfi-consent-management');
    }
  };
  const handleViewTPPCMI = () => {
    setDemoMode('TPP');
    setCurrentStep('consent-management');
  };
  const handleViewLFICMI = () => {
    setDemoMode('LFI');
    setCurrentStep('lfi-consent-management');
  };
  const handleResetDemo = () => {
    setActiveConsents(MOCK_DATA.active);
    setHistoryConsents(MOCK_DATA.history);
    setPendingConsents(MOCK_DATA.pending);
    setConsentCreated(false);
    setSelectedBank(null);
    setSelectedAccounts([]);
    setSelectedBill(null);
    setVRPConfig(null);
    setVRPPaymentAccount(null);
    setCurrentStep('demo-landing');
  };
  const handleConnectBank = () => {
    setCurrentStep('provider-selection');
  };
  const handleSelectBank = (bank: Bank) => {
    setSelectedBank(bank);
    setCurrentStep('consent-duration');
  };
  const handleDurationSelected = (durationMonths: number) => {
    setConsentDuration(durationMonths);
    setCurrentStep('consent-details');
  };
  const handleProceed = () => {
    setCurrentStep('redirect-to-lfi');
    setTimeout(() => setCurrentStep('mfa-verification'), 2000);
  };
  const handleAuthenticated = () => {
    setCurrentStep('mfa-verification');
  };
  const handleMFAVerified = () => {
    setCurrentStep('account-selection');
  };
  const handleAccountsSelected = (accountIds: string[]) => {
    setSelectedAccounts(accountIds);
    // Create consent immediately when accounts are selected
    if (selectedBank && !consentCreated) {
      const newConsent: ConsentDetails = {
        id: Date.now().toString(),
        consentId: `${Date.now()}${Math.random().toString(36).substring(7)}`,
        type: 'DataSharing',
        state: 'Authorized',
        tppName,
        tppTradingName: tppName,
        tppLegalName: 'VeloX Financial Services LLC',
        tppLogo: VELOX_LOGO,
        bankName: selectedBank.name,
        bankLogo: selectedBank.logoUrl,
        accountType: 'Current Account',
        accountNumber: '4627033234562456',
        accountLastFour: '2456',
        connectedDate: new Date().toLocaleDateString('en-GB'),
        expiryDate,
        lastAccessDate: new Date().toISOString(),
        durationMonths: consentDuration,
        dataClusters: UAE_DATA_CLUSTERS.slice(0, 5),
        selectedAccounts: MOCK_ACCOUNTS.filter(acc => selectedAccounts.includes(acc.id)).map(acc => ({
          id: acc.id,
          name: acc.name,
          accountNumber: acc.accountNumber,
          accountType: acc.name
        })),
        createdBy: 'User',
        lastModified: new Date().toISOString(),
        version: 1
      };
      setActiveConsents([...activeConsents, newConsent]);
      setConnectedBanks([...connectedBanks, selectedBank]);
      setConsentCreated(true);
    }
    setCurrentStep('redirect-to-tpp');
    setTimeout(() => setCurrentStep('success'), 2000);
  };
  const handleViewConsentManagement = () => {
    setDemoMode('TPP');
    setCurrentStep('consent-management');
  };
  const handleAddMoreAccounts = () => {
    setConsentCreated(false);
    setSelectedBank(null);
    setSelectedAccounts([]);
    setCurrentStep('provider-selection');
  };
  const handleViewLFIConsentManagement = () => {
    setDemoMode('LFI');
    setCurrentStep('lfi-consent-management');
  };
  const handleViewConsentDetails = (consentId: string) => {
    setSelectedConsentId(consentId);
    setCurrentStep('consent-detailed-view');
  };
  const handleViewLFIConsentDetails = (consentId: string) => {
    setSelectedConsentId(consentId);
    setCurrentStep('lfi-consent-detailed-view');
  };
  const handleRevokeConsent = (consentId: string) => {
    const consent = activeConsents.find(c => c.id === consentId);
    if (consent) {
      const revokedConsent: ConsentDetails = {
        ...consent,
        state: 'Canceled',
        canceledDate: new Date().toLocaleDateString('en-GB')
      };
      setActiveConsents(activeConsents.filter(c => c.id !== consentId));
      setHistoryConsents([...historyConsents, revokedConsent]);
    }
    setCurrentStep('consent-management');
  };
  const handlePauseConsent = () => {
    if (selectedConsentId) {
      setActiveConsents(activeConsents.map(c => c.id === selectedConsentId ? {
        ...c,
        state: 'Paused' as ConsentState
      } : c));
    }
    setCurrentStep('consent-management');
  };
  const handleReactivateConsent = () => {
    if (selectedConsentId) {
      setActiveConsents(activeConsents.map(c => c.id === selectedConsentId ? {
        ...c,
        state: 'Authorized' as ConsentState
      } : c));
    }
    setCurrentStep('consent-management');
  };
  // LFI CMI handlers
  const handleAuthorizeConsent = (consentId: string) => {
    const consent = pendingConsents.find(c => c.id === consentId);
    if (consent) {
      const authorizedConsent: ConsentDetails = {
        ...consent,
        state: 'Authorized',
        authorizedDate: new Date().toLocaleDateString('en-GB')
      };
      setPendingConsents(pendingConsents.filter(c => c.id !== consentId));
      setActiveConsents([...activeConsents, authorizedConsent]);
    }
    setCurrentStep('lfi-consent-management');
  };
  const handleRejectConsent = (consentId: string) => {
    const consent = pendingConsents.find(c => c.id === consentId);
    if (consent) {
      const rejectedConsent: ConsentDetails = {
        ...consent,
        state: 'Rejected'
      };
      setPendingConsents(pendingConsents.filter(c => c.id !== consentId));
      setHistoryConsents([...historyConsents, rejectedConsent]);
    }
    setCurrentStep('lfi-consent-management');
  };
  const handleSuspendConsent = (consentId: string) => {
    setActiveConsents(activeConsents.map(c => c.id === consentId ? {
      ...c,
      state: 'Suspended' as ConsentState
    } : c));
    setCurrentStep('lfi-consent-management');
  };
  const handleRevokeLFIConsent = (consentId: string) => {
    const consent = activeConsents.find(c => c.id === consentId);
    if (consent) {
      const revokedConsent: ConsentDetails = {
        ...consent,
        state: 'Canceled',
        canceledDate: new Date().toLocaleDateString('en-GB')
      };
      setActiveConsents(activeConsents.filter(c => c.id !== consentId));
      setHistoryConsents([...historyConsents, revokedConsent]);
    }
    setCurrentStep('lfi-consent-management');
  };
  const handleError = (code: ErrorCode, message?: string) => {
    setErrorState({
      code,
      message
    });
    setCurrentStep('error');
  };
  const handleRetryFromError = () => {
    setErrorState(null);
    if (errorState?.code === 'AUTH_FAILED') {
      setCurrentStep('authentication');
    } else if (errorState?.code === 'SESSION_EXPIRED') {
      setCurrentStep('provider-selection');
    } else {
      setCurrentStep('consent-details');
    }
  };
  const handleBack = () => {
    const stepFlow: Step[] = ['pfm-before', 'provider-selection', 'consent-duration', 'consent-details', 'redirect-to-lfi', 'mfa-verification', 'account-selection', 'redirect-to-tpp', 'success', 'pfm-after'];
    const currentIndex = stepFlow.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepFlow[currentIndex - 1]);
    }
  };
  const getProgressStep = (): 1 | 2 | 3 => {
    switch (currentStep) {
      case 'provider-selection':
      case 'consent-duration':
      case 'consent-details':
      case 'redirect-to-lfi':
        return 1;
      case 'mfa-verification':
      case 'account-selection':
      case 'redirect-to-tpp':
        return 2;
      case 'success':
        return 3;
      default:
        return 1;
    }
  };
  const getContext = (): 'TPP' | 'LFI' => {
    return ['mfa-verification', 'account-selection'].includes(currentStep) ? 'LFI' : 'TPP';
  };
  const showProgress = !['pfm-before', 'pfm-after', 'consent-management', 'consent-detailed-view', 'lfi-consent-management', 'lfi-consent-detailed-view', 'redirect-to-lfi', 'mfa-verification', 'redirect-to-tpp', 'bill-selection', 'vrp-setup', 'vrp-permission', 'vrp-redirect-to-lfi', 'vrp-bank-auth', 'vrp-account-selection', 'vrp-redirect-to-tpp', 'vrp-success', 'error'].includes(currentStep);
  const getBankColor = () => {
    if (selectedBank) {
      const theme = getBankTheme(selectedBank.id);
      return theme ? theme.gradients.header : undefined;
    }
    return undefined;
  };
  // Show demo landing screen
  if (currentStep === 'demo-landing') {
    return <DemoLandingScreen onStartDataSharingJourney={handleStartDataSharingJourney} onStartVRPJourney={handleStartVRPJourney} onViewTPPCMI={handleViewTPPCMI} onViewLFICMI={handleViewLFICMI} onResetDemo={handleResetDemo} onSkipToApp={onSkipToApp} activeCount={activeConsents.length} pendingCount={pendingConsents.length} historyCount={historyConsents.length} data-id="element-8" />;
  }
  // Show PFM screens without consent layout wrapper
  if (currentStep === 'pfm-before') {
    return <>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-9">
          <div className="w-full max-w-md" data-id="element-10">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-11">
              <PFMBeforeScreen onConnectBank={handleConnectBank} data-id="element-12" />
            </div>
          </div>
        </div>
      </>;
  }
  if (currentStep === 'pfm-after') {
    return <div className="min-h-screen bg-gray-50" data-id="element-13">
        <CMITopBar title="Dashboard" onBack={() => setCurrentStep('demo-landing')} showBackButton={false} data-id="element-14" />
        <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-15">
          <PFMAfterScreen connectedBanks={connectedBanks} onAddMoreAccounts={handleAddMoreAccounts} onManageConsents={handleViewConsentManagement} onPayBill={handlePayBill} onSetupAutoPay={handleSetupAutoPay} data-id="element-16" />
          <div className="mt-6 pt-6 border-t-2 border-gray-200 space-y-2" data-id="element-17">
            <button onClick={handleViewConsentManagement} className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 transition-colors" data-id="element-18">
              TPP View: Manage Consents
            </button>
            <button onClick={handleViewLFIConsentManagement} className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-xl text-sm font-medium text-blue-700 transition-colors" data-id="element-19">
              🏦 LFI View: Bank Consent Management
            </button>
          </div>
        </div>
      </div>;
  }
  // Bill Selection Screen (VRP Journey Start)
  if (currentStep === 'bill-selection') {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-20">
        <div className="w-full max-w-md" data-id="element-21">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-22">
            <BillSelectionScreen bills={MOCK_DATA.upcomingBills} onSelectBill={handleBillSelected} onCancel={() => setCurrentStep('demo-landing')} data-id="element-23" />
          </div>
        </div>
      </div>;
  }
  // VRP Provider Selection Screen (when connecting new bank)
  if (currentStep === 'vrp-provider-selection') {
    return <ConsentLayout currentStep={1} context="TPP" showProgress={true} tppLogoUrl={VELOX_LOGO} onBack={() => setCurrentStep('vrp-setup')} data-id="element-24">
        <ProviderSelectionScreen banks={MOCK_BANKS} onSelectBank={handleVRPBankSelected} data-id="element-25" />
      </ConsentLayout>;
  }
  // Bank Connection Selection Screen (VRP Journey)
  if (currentStep === 'bank-connection-selection' && selectedBill) {
    // Convert connectedBanks to ConnectedBank format
    const connectedBanksList: ConnectedBank[] = connectedBanks.map(bank => ({
      id: bank.id,
      name: bank.name,
      logoUrl: bank.logoUrl,
      accountType: 'Current Account',
      accountLastFour: '2456',
      connectedDate: new Date().toLocaleDateString('en-GB')
    }));
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-26">
        <div className="w-full max-w-md" data-id="element-27">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-28">
            <BankConnectionSelectionScreen merchantName={selectedBill.merchantName} merchantLogo={selectedBill.merchantLogo} connectedBanks={connectedBanksList} onSelectExistingBank={handleSelectExistingBank} onConnectNewBank={handleConnectNewBank} onCancel={() => setCurrentStep('bill-selection')} data-id="element-29" />
          </div>
        </div>
      </div>;
  }
  // VRP Flow Screens
  if (currentStep === 'vrp-setup' && selectedBill) {
    // Convert connectedBanks to ConnectedBank format
    const connectedBanksList: ConnectedBank[] = connectedBanks.map(bank => ({
      id: bank.id,
      name: bank.name,
      logoUrl: bank.logoUrl,
      accountType: 'Current Account',
      accountLastFour: '2456',
      connectedDate: new Date().toLocaleDateString('en-GB')
    }));
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-30">
        <div className="w-full max-w-md" data-id="element-31">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-32">
            <VRPSetupScreen merchantName={selectedBill.merchantName} merchantLogo={selectedBill.merchantLogo} billAmount={selectedBill.amount} currency={selectedBill.currency} connectedBanks={connectedBanksList} onContinueWithBank={(config, bankId) => handleVRPConfigured(config, bankId)} onContinueWithNewBank={config => handleVRPConfigured(config)} onCancel={() => setCurrentStep('bill-selection')} data-id="element-33" />
          </div>
        </div>
      </div>;
  }
  if (currentStep === 'vrp-permission' && selectedBill && vrpConfig) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-34">
        <div className="w-full max-w-md" data-id="element-35">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-36">
            <VRPPermissionScreen merchantName={selectedBill.merchantName} merchantLogo={selectedBill.merchantLogo} bankName={currentBank.name} bankLogo={currentBank.logoUrl} accountType="Current Account" accountLastFour="2456" vrpConfig={vrpConfig} currency={selectedBill.currency} reference={selectedBill.reference} paymentPurpose="utility-bill" onAuthorize={handleVRPAuthorized} onCancel={() => setCurrentStep('vrp-setup')} data-id="element-37" />
          </div>
        </div>
      </div>;
  }
  // VRP Redirect to Bank
  if (currentStep === 'vrp-redirect-to-lfi' && selectedBank) {
    return <ConsentLayout currentStep={1} context="TPP" showProgress={true} tppLogoUrl={VELOX_LOGO} data-id="element-38">
        <RedirectionScreen targetName={selectedBank.name} targetType="LFI" logoUrl={selectedBank.logoUrl} data-id="element-39" />
      </ConsentLayout>;
  }
  // VRP Bank Authentication (Face ID)
  if (currentStep === 'vrp-bank-auth' && selectedBank) {
    return <ConsentLayout currentStep={2} context="LFI" showProgress={true} bankName={selectedBank.name} bankLogoUrl={selectedBank.logoUrl} bankColor={getBankTheme(selectedBank.id)?.colors.primary || DEFAULT_BANK_THEME.colors.primary} data-id="element-40">
        <FaceIDScreen bankName={selectedBank.name} onAuthenticated={handleVRPBankAuthComplete} bankTheme={getBankTheme(selectedBank.id) || DEFAULT_BANK_THEME} data-id="element-41" />
      </ConsentLayout>;
  }
  if (currentStep === 'vrp-authorization' && selectedBill && vrpConfig && selectedBank) {
    return <ConsentLayout currentStep={2} context="LFI" showProgress={true} bankName={selectedBank.name} bankLogoUrl={selectedBank.logoUrl} bankColor={getBankTheme(selectedBank.id)?.colors.primary || DEFAULT_BANK_THEME.colors.primary} onBack={() => setCurrentStep('vrp-bank-auth')} data-id="element-42">
        <VRPAuthorizationScreen tppName={tppName} merchantName={selectedBill.merchantName} merchantLogo={selectedBill.merchantLogo} vrpConfig={vrpConfig} currency={selectedBill.currency} reference={selectedBill.reference} paymentPurpose="utility-bill" accounts={MOCK_ACCOUNTS} onAuthorize={handleVRPAuthorizationComplete} onCancel={() => setCurrentStep('vrp-bank-auth')} data-id="element-43" />
      </ConsentLayout>;
  }
  if (currentStep === 'vrp-redirect-to-tpp' && selectedBank) {
    return <ConsentLayout currentStep={2} context="LFI" showProgress={true} bankName={selectedBank.name} bankLogoUrl={selectedBank.logoUrl} bankColor={getBankTheme(selectedBank.id)?.colors.primary || DEFAULT_BANK_THEME.colors.primary} data-id="element-44">
        <RedirectionScreen targetName={tppName} targetType="TPP" logoUrl={VELOX_LOGO} sourceName={selectedBank.name} sourceLogoUrl={selectedBank.logoUrl} data-id="element-45" />
      </ConsentLayout>;
  }
  if (currentStep === 'vrp-tpp-auth' && selectedBank) {
    return <ConsentLayout currentStep={2} context="LFI" showProgress={true} bankName={selectedBank.name} bankLogoUrl={selectedBank.logoUrl} bankColor={getBankTheme(selectedBank.id)?.colors.primary || DEFAULT_BANK_THEME.colors.primary} data-id="element-46">
        <FaceIDScreen bankName={selectedBank.name} onAuthenticated={handleVRPTPPAuthComplete} bankTheme={getBankTheme(selectedBank.id) || DEFAULT_BANK_THEME} data-id="element-47" />
      </ConsentLayout>;
  }
  if (currentStep === 'vrp-success' && selectedBill && vrpConfig) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-48">
        <div className="w-full max-w-md" data-id="element-49">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6" data-id="element-50">
            <VRPSuccessScreen merchantName={selectedBill.merchantName} merchantLogo={selectedBill.merchantLogo} vrpConfig={vrpConfig} currency={selectedBill.currency} nextPaymentDate={selectedBill.dueDate} onViewDashboard={handleViewDashboard} onManageConsents={handleViewConsentManagement} data-id="element-51" />
          </div>
        </div>
      </div>;
  }
  // TPP Consent Management Screen (full-screen, no wrapper)
  if (currentStep === 'consent-management') {
    return <div className="min-h-screen bg-gray-50" data-id="element-52">
        <CMITopBar title="Manage Consents" onBack={() => setCurrentStep('demo-landing')} showBackButton={true} data-id="element-53" />
        <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-54">
          <ConsentManagementScreen activeConsents={activeConsents} historyConsents={historyConsents} onViewDetails={handleViewConsentDetails} onUpdateConsent={id => console.log('Update:', id)} onRevokeConsent={handleRevokeConsent} onAddNewConnection={handleConnectBank} data-id="element-55" />
        </div>
      </div>;
  }
  // TPP Consent Detailed View (full-screen, no wrapper)
  if (currentStep === 'consent-detailed-view' && selectedConsentId) {
    const consent = [...activeConsents, ...historyConsents].find(c => c.id === selectedConsentId);
    if (!consent) {
      setCurrentStep('consent-management');
      return null;
    }
    return <div className="min-h-screen bg-gray-50" data-id="element-56">
        <CMITopBar title="Consent Details" onBack={() => setCurrentStep('consent-management')} showBackButton={true} data-id="element-57" />
        <div className="max-w-4xl mx-auto px-4 py-6" data-id="element-58">
          <ConsentDetailedView consent={consent} onBack={() => setCurrentStep('consent-management')} onRevoke={() => handleRevokeConsent(selectedConsentId)} onPause={handlePauseConsent} onReactivate={handleReactivateConsent} context="TPP" data-id="element-59" />
        </div>
      </div>;
  }
  // LFI Consent Management Screen (full-screen, no wrapper)
  if (currentStep === 'lfi-consent-management') {
    return <div className="min-h-screen bg-gray-50" data-id="element-60">
        <CMITopBar title={`${getShortBankName(currentBank.name)} - Manage Consents`} onBack={() => setCurrentStep('demo-landing')} showBackButton={true} data-id="element-61" />
        <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-62">
          <LFIConsentManagementScreen bankId={currentBank.id} bankName={currentBank.name} activeConsents={activeConsents} pendingConsents={pendingConsents} historyConsents={historyConsents} onViewDetails={handleViewLFIConsentDetails} onAuthorizeConsent={handleAuthorizeConsent} onRejectConsent={handleRejectConsent} onRevokeConsent={handleRevokeLFIConsent} onSuspendConsent={handleSuspendConsent} data-id="element-63" />
        </div>
      </div>;
  }
  // LFI Consent Detailed View (full-screen, no wrapper)
  if (currentStep === 'lfi-consent-detailed-view' && selectedConsentId) {
    const consent = [...activeConsents, ...pendingConsents, ...historyConsents].find(c => c.id === selectedConsentId);
    if (!consent) {
      setCurrentStep('lfi-consent-management');
      return null;
    }
    return <div className="min-h-screen bg-gray-50" data-id="element-64">
        <CMITopBar title="Consent Details" onBack={() => setCurrentStep('lfi-consent-management')} showBackButton={true} data-id="element-65" />
        <div className="max-w-4xl mx-auto px-4 py-6" data-id="element-66">
          <LFIConsentDetailedView consent={consent} bankId={currentBank.id} bankName={currentBank.name} onBack={() => setCurrentStep('lfi-consent-management')} onAuthorize={consent.state === 'Awaiting' ? () => handleAuthorizeConsent(selectedConsentId) : undefined} onReject={consent.state === 'Awaiting' ? () => handleRejectConsent(selectedConsentId) : undefined} onRevoke={['Authorized', 'Suspended'].includes(consent.state) ? () => handleRevokeLFIConsent(selectedConsentId) : undefined} onSuspend={consent.state === 'Authorized' ? () => handleSuspendConsent(selectedConsentId) : undefined} data-id="element-67" />
        </div>
      </div>;
  }
  // Consent flow screens (wrapped in ConsentLayout)
  return <>
      <ConsentLayout currentStep={getProgressStep()} context={getContext()} onBack={currentStep !== 'provider-selection' && currentStep !== 'success' ? handleBack : undefined} showProgress={showProgress} bankName={selectedBank?.name} bankLogoUrl={selectedBank?.logoUrl} bankColor={getBankColor()} tppLogoUrl={VELOX_LOGO} data-id="element-68">
        {currentStep === 'provider-selection' && <ProviderSelectionScreen banks={MOCK_BANKS} onSelectBank={handleSelectBank} data-id="element-69" />}

        {currentStep === 'consent-duration' && <ConsentDurationScreen tppName={tppName} onContinue={handleDurationSelected} requiresHistoricalData={true} data-id="element-70" />}

        {currentStep === 'consent-details' && selectedBank && <ConsentDetailsScreen tppName={tppName} bankName={selectedBank.name} expiryDate={expiryDate} permissions={MOCK_PERMISSIONS} onProceed={handleProceed} durationMonths={consentDuration} data-id="element-71" />}

        {currentStep === 'redirect-to-lfi' && selectedBank && <RedirectionScreen targetName={selectedBank.name} targetType="LFI" logoUrl={selectedBank.logoUrl} data-id="element-72" />}

        {currentStep === 'mfa-verification' && selectedBank && <FaceIDScreen bankName={selectedBank.name} onAuthenticated={handleMFAVerified} bankTheme={bankTheme} data-id="element-73" />}

        {currentStep === 'account-selection' && selectedBank && <AccountSelectionScreen tppName={tppName} accounts={MOCK_ACCOUNTS} unavailableAccounts={MOCK_UNAVAILABLE_ACCOUNTS} expiryDate={expiryDate} permissions={MOCK_LFI_PERMISSIONS} hasAccountSpecificPermissions={true} onAuthorize={handleAccountsSelected} onCancel={() => setCurrentStep('provider-selection')} bankTheme={bankTheme} data-id="element-74" />}

        {currentStep === 'redirect-to-tpp' && selectedBank && <RedirectionScreen targetName={tppName} targetType="TPP" logoUrl={VELOX_LOGO} sourceName={selectedBank.name} sourceLogoUrl={selectedBank.logoUrl} data-id="element-75" />}

        {currentStep === 'success' && selectedBank && <ConsentSuccessScreen bankName={selectedBank.name} selectedAccountsCount={selectedAccounts.length} expiryDate={expiryDate} onViewPlan={handleViewConsentManagement} onViewDashboard={handleViewDashboard} data-id="element-76" />}

        {currentStep === 'error' && errorState && <ErrorScreen errorCode={errorState.code} errorMessage={errorState.message} requestId={`REQ-${Date.now()}`} onRetry={handleRetryFromError} onCancel={() => setCurrentStep('pfm-before')} data-id="element-77" />}
      </ConsentLayout>
    </>;
}